"use client"

import { useRouter } from "@bprogress/next/app";
import { Accordion, AccordionDetails, AccordionDetailsProps, AccordionProps, AccordionSummary, Box, Button, CircularProgress, colors, Drawer, IconButton, NoSsr, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { SxProps } from "@mui/system";
import CenterBox from "@v2/components/styled/CenterBox";
import FlexBox from "@v2/components/styled/FlexBox";
import StyledBadge from "@v2/components/StyledBadge";
import useClientHasMouse from "@v2/hooks/useClientHasMouse";
import { uiColors } from "@v2/lib/color";
import { ChevronDown, CirclePercent, Funnel, FunnelPlus, Warehouse, X } from "lucide-react";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { Fragment, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { pruneParamsForCategory } from "../categorySwitch";
import { FilterRefProperties, FilterValueTypesMap, PLPFilters, PLPSearchParams, PLPSpecialBrand, PLPSpecialCollection } from "../types";
import { requestPlpHeaderScroll } from "./scrollToPlpHeader";

const LoadingFilter = () => {
    return <CenterBox>
        <CircularProgress disableShrink size={20} />
    </CenterBox>
}

const SelectFilter = dynamic(() => import('./filters/SelectFilter'), { ssr: false, loading: () => <LoadingFilter /> });
const BooleanFilter = dynamic(() => import('./filters/BooleanFilter'), { ssr: false, loading: () => <LoadingFilter /> });
const BrandFilter = dynamic(() => import('./filters/BrandFilter'), { ssr: false, loading: () => <LoadingFilter /> });
const ColorFilter = dynamic(() => import('./filters/ColorFilter'), { ssr: false, loading: () => <LoadingFilter /> });
const PriceFilter = dynamic(() => import('./filters/PriceFilter'), { ssr: false, loading: () => <LoadingFilter /> });
const SortFilter = dynamic(() => import('./filters/SortFilter'), { ssr: false, loading: () => <LoadingFilter /> });
const ProductClassFilter = dynamic(() => import('./filters/ProductClassFilter'), { ssr: false, loading: () => <LoadingFilter /> });

function getSearchParamsKey(searchParams: PLPSearchParams) {
    const params = new URLSearchParams();
    Object.entries(searchParams).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.forEach((item) => params.append(key, item));
            return;
        }
        if (value !== undefined) params.set(key, value);
    });
    return params.toString();
}

// "compact" shrinks the trigger below md, where the toolbar stacks it above another row.
export default function PlpFilter({ filters, searchParams, eqPathSearchParams, specialBrand, specialCollection, compact = false, hideSort = false }: {
    filters: PLPFilters,
    searchParams: PLPSearchParams,
    eqPathSearchParams: PLPSearchParams,
    specialBrand: PLPSpecialBrand,
    specialCollection: PLPSpecialCollection,
    compact?: boolean,
    hideSort?: boolean,
}) {
    const router = useRouter()
    const theme = useTheme()
    const pathname = usePathname()
    const hasMouse = useClientHasMouse()
    const [open, setOpen] = useState(false)
    const [expanded, setExpanded] = useState<{ [key in string]: boolean }>({});
    const filtersRef = useRef<{ [key in string]: FilterRefProperties<FilterValueTypesMap> }>({})
    const isFullScreenFilter = useMediaQuery(theme.breakpoints.down('md'))
    const filterInputsKey = getSearchParamsKey(searchParams);

    useEffect(() => {
        filtersRef.current = {};
    }, [filterInputsKey]);

    useEffect(() => {
        if (open && isFullScreenFilter) {
            window.history.pushState({ modal: false }, "");
            const handlePopState = () => {
                closeHandler();
            };
            window.addEventListener("popstate", handlePopState);
            return () => {
                window.removeEventListener("popstate", handlePopState);
                if (window.history.state?.modal) {
                    window.history.back();
                }
            };
        }
    }, [open, isFullScreenFilter]);

    useEffect(() => {
        closeHandler()
    }, [pathname])

    const filterOverTitle = filters.type !== "global" && filters.product_class
        ? filters.product_class?.site_name + (specialBrand ? " " + specialBrand?.name : "")
        : specialBrand
            ? `برند ${specialBrand.name}`
            : specialCollection
                ? `کالکشن ${specialCollection.title}`
                : 'همه دسته بندی ها'

    const closeHandler = useCallback(() => {
        setOpen(false)
        // applyFilters()
    }, [searchParams])

    const openHandler = useCallback(() => {
        setOpen(true)
    }, [])

    const handleChangeExpanded = useCallback((panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(ov => {
            return { ...ov, [panel]: isExpanded }
        });
    }, []);

    const countableFilters = Object.keys(searchParams)
        .filter(item => !['sort', 'page', 'limit', 'lp', 'search', 'brand_slug', 'collection_slug', 'slug', 'pd'].includes(item))
        .filter(item => !Object.keys(eqPathSearchParams).includes(item))

    const applyFilters = useCallback(() => {
        const params = new URLSearchParams(Object.entries(searchParams));
        params.delete('lp')
        params.delete('page')
        if (!pathname.startsWith('/fa/products') && !pathname.startsWith('/fa/brand')) {
            // in eq-paths product classes is in eq-path target and do not need to url
            params.delete('product_class_slug')
        }
        if (pathname.startsWith('/fa/bran')) {
            params.delete('brand_slug')
            params.delete('slug')
        }

        const currentSearchParams = params.toString()
        Object.keys(filtersRef.current).forEach(key => {
            const filter = filtersRef.current[key]
            filter?.apply(params)
        })

        Object.keys(eqPathSearchParams).forEach(key => {
            if (params.has(key)) {
                params.delete(key)
            }
        })

        // Picking a category in here is a category switch like any other, so it
        // drops the filters the new one cannot answer for. Only a real change
        // prunes: editing a colour or a price alone must keep what it edited.
        const previousProductClassSlug = Array.isArray(searchParams.product_class_slug)
            ? [...searchParams.product_class_slug].reverse()[0]
            : searchParams.product_class_slug
        const nextProductClassSlug = params.get('product_class_slug')

        if (nextProductClassSlug && nextProductClassSlug !== previousProductClassSlug) {
            pruneParamsForCategory(
                params,
                filters.product_classes?.find(item => item.slug === nextProductClassSlug),
            )
        }

        const search = params.toString();
        if (currentSearchParams !== search) {
            const query = search ? `?${search}` : "";
            // const path = pathname.startsWith('/fa/brand') ? pathname : '/fa/products'

            requestPlpHeaderScroll();
            router.push(`${pathname}${query}`, { scroll: false });
        }

    }, [eqPathSearchParams, searchParams, pathname, filters])

    const clearFilterInputs = useCallback(() => {
        Object.keys(filtersRef.current).forEach(key => {
            filtersRef.current[key]?.clear()
        })
    }, [])

    const clearFilters = useCallback(() => {

        setOpen(false)

        const params = new URLSearchParams();

        // const currentPrdClsSlug = searchParams.product_class_slug
        // if (currentPrdClsSlug) {
        //     params.set('product_class_slug', currentPrdClsSlug)
        // }

        clearFilterInputs()



        const search = params.toString();
        const query = search ? `?${search}` : "";

        // const path = pathname.startsWith('/fa/brand') ? pathname : '/fa/products'

        router.push(`${pathname}${query}`, { scroll: false });

    }, [pathname, searchParams])

    useEffect(() => {
        if (Object.keys(searchParams).filter(i => !['product_class_slug', 'page', 'limit', 'lp'].includes(i)).length === 0) clearFilterInputs()
    }, [searchParams])


    const applyFilterAction = useCallback(() => {
        applyFilters()
        setOpen(false)

    }, [searchParams])

    const currentProductClassSlug = Array.isArray(searchParams.product_class_slug)
        ? [...searchParams.product_class_slug].reverse()[0]
        : searchParams.product_class_slug;

    return <Fragment>

        <NoSsr fallback={
            <Skeleton variant="rounded" height={58} width={138} sx={{
                display: 'inline-block',
                [theme.breakpoints.down('md')]: {
                    width: compact ? `92px !important` : `95px !important`,
                    height: compact ? `36px !important` : `48px !important`
                },
            }} />
        }>

            <StyledBadge anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }} size="large" badgeContent={countableFilters.length} >
                <Button variant="outlined" color="inherit" sx={{
                    height: 58,
                    width: 138,
                    justifyContent: 'space-around',
                    zIndex: 20,
                    [theme.breakpoints.down('md')]: {
                        width: compact ? 92 : 95,
                        height: compact ? 36 : 48,
                    },

                }}
                    onClick={openHandler}
                    endIcon={<Funnel />}>
                    فیلتر
                </Button>
            </StyledBadge>


            <Drawer
                slotProps={{
                    paper: {
                        sx: {
                            left: 'unset',
                            right: 0,
                            width: {
                                xs: '100%',
                                md: 534
                            }
                        }
                    }
                }}

                // disableSwipeToOpen
                // disableDiscovery
                disableScrollLock={hasMouse}
                keepMounted
                anchor={'left'}
                open={open}
                onClose={closeHandler}
            // onOpen={openHandler}
            >
                {/* ################## SWIPER HEADER ################## */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minHeight: 56,
                    px: 1,
                    borderBottom: `1px solid ${colors.grey[200]}`,
                    // boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                    position: 'sticky',
                    top: 0,
                    bgcolor: '#fff',
                    zIndex: 2,
                }}>
                    <FlexBox>
                        <IconButton sx={{ height: 40, width: 40 }} onClick={closeHandler}>
                            <X />
                        </IconButton>
                        <Box>
                            <Typography component="span" sx={{ fontSize: { xs: 15, sm: 18 }, fontWeight: 600, ml: 1 }}>
                                فیلترها
                            </Typography>
                            <Typography component="span" sx={{ display: 'inline-block', fontSize: { xs: 12, sm: 15 }, fontWeight: 400, color: colors.grey[700], ml: 1 }}>
                                {filterOverTitle}
                            </Typography>
                        </Box>
                    </FlexBox>
                    <Button sx={{
                        fontSize: { xs: 10, sm: 13 },
                        minWidth: 95
                    }}
                        disableElevation
                        disableRipple
                        onClick={clearFilters}
                        disabled={countableFilters.length < 1}
                    >
                        حذف فیلترها
                    </Button>
                </Box>
                {/* ################## END SWIPER HEADER ################## */}

                <Box sx={{
                    height: 'calc(100vh - 120px)',
                    width: '100%',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${colors.grey[800]} ${colors.grey[300]}`,
                    '&::-webkit-scrollbar': { width: '4px' },
                    '&::-webkit-scrollbar-track': { background: colors.grey[300] },
                    '&::-webkit-scrollbar-thumb': { background: colors.grey[800] },
                }}>
                    <Box key={filterInputsKey} sx={{
                        px: 2,
                        '& .MuiAccordionSummary-content': {
                            alignItems: 'center'
                        }
                    }}>
                        {/* Only for toolbars that hide their own sort control on small screens. */}
                        {!hideSort && (
                            <Box sx={{
                                display: {
                                    xs: 'block',
                                    sm: 'none'
                                }
                            }}>

                                <FilterTitleWrapper sx={{
                                    height: 40
                                }} used={!!(searchParams.sort)}>
                                    <FilterTitle>مرتب سازی بر اساس</FilterTitle>
                                </FilterTitleWrapper>
                                <SortFilter
                                    searchParamsKey="sort"
                                    ref={el => { filtersRef.current['sort'] = el }}
                                    defaultValue={searchParams.sort}
                                />
                            </Box>
                        )}

                        {(!searchParams.search && !eqPathSearchParams['product_class_slug'] && filters.product_classes.length > 1) && (
                            <StyledAccordion
                                expanded={expanded['product_class'] ?? false}
                                onChange={handleChangeExpanded('product_class')}
                            >
                                <AccordionSummary expandIcon={<ChevronDown />} sx={{ p: 0 }} >
                                    <FilterTitle>دسته‌بندی</FilterTitle>
                                    {searchParams.product_class_slug && (
                                        <Dot />
                                    )}
                                </AccordionSummary>
                                <WithScrollAccordionDetails>
                                    <ProductClassFilter
                                        searchParamsKey="product_class_slug"
                                        ref={el => { filtersRef.current['product_class_slug'] = el }}
                                        productClasses={filters.product_classes}
                                        defaultValue={currentProductClassSlug}
                                    />
                                </WithScrollAccordionDetails>
                            </StyledAccordion>
                        )}

                        {(!eqPathSearchParams['brand'] && !searchParams['brand_slug'] && filters.brands.length > 1) && (
                            <StyledAccordion
                                expanded={expanded['brand'] ?? false}
                                onChange={handleChangeExpanded('brand')}
                            >
                                <AccordionSummary expandIcon={<ChevronDown />} sx={{ p: 0 }} >
                                    <FilterTitle>برند</FilterTitle>
                                    {searchParams.brand && (
                                        <Dot />
                                    )}
                                </AccordionSummary>
                                <WithScrollAccordionDetails>
                                    <BrandFilter
                                        searchParamsKey={'brand'}
                                        ref={el => { filtersRef.current['brand'] = el }}
                                        brands={filters.brands}
                                        defaultValue={searchParams.brand?.split(',')}
                                    />
                                </WithScrollAccordionDetails>
                            </StyledAccordion>
                        )}

                        {(filters.prices.min > 0 && filters.prices.max > 0) && (filters.prices.max !== filters.prices.min) && (

                            <Box sx={{
                                borderBottom: `1px solid ${colors.grey[300]}`,
                                pb: 2
                            }}>
                                <FilterTitleWrapper used={!!(searchParams.minp || searchParams.maxp)}>
                                    <FilterTitle>قیمت</FilterTitle>
                                </FilterTitleWrapper>
                                <PriceFilter
                                    ref={el => { filtersRef.current[`price`] = el }}
                                    min={filters.prices.min}
                                    max={filters.prices.max}
                                    defaultValue={{
                                        min: searchParams.minp ? parseInt(searchParams.minp) : null,
                                        max: searchParams.maxp ? parseInt(searchParams.maxp) : null
                                    }}
                                />
                            </Box>
                        )}

                        <Fragment>
                            {[...filters.attributes]
                                .filter(i => i.attribute.type !== 'boolean')
                                .filter(i => !eqPathSearchParams[`attr_${i.attribute.id}`])
                                .map((option) => {
                                    const searchParamsAttr = searchParams[`attr_${option.attribute.id}`];
                                    if (option.values.length < 2) {
                                        return;
                                    }
                                    return (
                                        <StyledAccordion key={option.attribute.id}
                                            slotProps={{
                                                transition: {
                                                    unmountOnExit: true
                                                }
                                            }}
                                            expanded={expanded[option.attribute.title] ?? false}
                                            onChange={handleChangeExpanded(option.attribute.title)}
                                        >
                                            <AccordionSummary expandIcon={<ChevronDown />} sx={{ p: 0 }} >
                                                <FilterTitle>{option.attribute.title}</FilterTitle>
                                                {searchParamsAttr && (
                                                    <Dot />
                                                )}
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <SelectFilter
                                                    searchParamsKey={`attr_${option.attribute.id}`}
                                                    ref={el => { filtersRef.current[`attr_${option.attribute.id}`] = el }}
                                                    options={option.values} defaultValue={searchParamsAttr?.split(',')}
                                                    type={option.attribute.type}
                                                />
                                            </AccordionDetails>
                                        </StyledAccordion>
                                    )
                                })}
                        </Fragment>

                        {/* One filter per colour part, offering families; an older backend sends only frame and lens colours. */}
                        {filters.color_parts ? filters.color_parts.filter(part => part.families.length > 1).map(part => {
                            const searchParamsKey = `cf_${part.slug}` as const;
                            return (
                                <StyledAccordion key={part.slug} expanded={expanded[searchParamsKey] ?? false}
                                    onChange={handleChangeExpanded(searchParamsKey)}
                                >
                                    <AccordionSummary expandIcon={<ChevronDown />} sx={{ p: 0 }} >
                                        <FilterTitle>{part.filter_title}</FilterTitle>
                                        {searchParams[searchParamsKey] && (
                                            <Dot />
                                        )}
                                    </AccordionSummary>
                                    <WithScrollAccordionDetails>
                                        <ColorFilter
                                            searchParamsKey={searchParamsKey}
                                            options={part.families.map(family => ({ id: family.slug, code: family.code, texture: null, title: family.title }))}
                                            ref={el => { filtersRef.current[searchParamsKey] = el }}
                                            defaultValue={searchParams[searchParamsKey]?.split(',')}
                                        />
                                    </WithScrollAccordionDetails>
                                </StyledAccordion>
                            )
                        }) : <Fragment>
                            {filters.frame_colors?.length > 1 && (
                                <StyledAccordion expanded={expanded['frame_colors'] ?? false}
                                    onChange={handleChangeExpanded('frame_colors')}
                                >
                                    <AccordionSummary expandIcon={<ChevronDown />} sx={{ p: 0 }} >
                                        <FilterTitle>رنگ فریم</FilterTitle>
                                        {searchParams.fc && (
                                            <Dot />
                                        )}
                                    </AccordionSummary>
                                    <WithScrollAccordionDetails>
                                        <ColorFilter
                                            searchParamsKey="fc"
                                            options={filters.frame_colors}
                                            ref={el => { filtersRef.current[`frame_colors`] = el }}
                                            defaultValue={searchParams.fc?.split(',')}
                                        />
                                    </WithScrollAccordionDetails>
                                </StyledAccordion>
                            )}

                            {filters.lens_colors?.length > 1 && (
                                <StyledAccordion expanded={expanded['lens_colors'] ?? false}
                                    onChange={handleChangeExpanded('lens_colors')}
                                >
                                    <AccordionSummary expandIcon={<ChevronDown />} sx={{ p: 0 }} >
                                        <FilterTitle>رنگ لنز</FilterTitle>
                                        {searchParams.lc && (
                                            <Dot />
                                        )}
                                    </AccordionSummary>
                                    <WithScrollAccordionDetails>
                                        <ColorFilter
                                            searchParamsKey='lc'
                                            options={filters.lens_colors}
                                            ref={el => { filtersRef.current[`lens_colors`] = el }}
                                            defaultValue={searchParams.lc?.split(',')}
                                        />
                                    </WithScrollAccordionDetails>
                                </StyledAccordion>
                            )}
                        </Fragment>}


                        <Fragment>
                            {[...filters.attributes]
                                .filter(i => i.attribute.type == 'boolean')
                                .filter(i => eqPathSearchParams[`attr_${i.attribute.id}`] !== "1")
                                .map((option) => {
                                    return (
                                        <Box key={option.attribute.id}>
                                            {{
                                                boolean: <BooleanFilter
                                                    label={option.attribute.title}
                                                    defaultValue={searchParams[`attr_${option.attribute.id}`]}
                                                    searchParamsKey={`attr_${option.attribute.id}`}
                                                    ref={el => { filtersRef.current[`attr_${option.attribute.id}`] = el }} />,
                                            }[option.attribute.type]}
                                        </Box>
                                    )
                                })}
                            <BooleanFilter
                                label={
                                    <Fragment>
                                        <CirclePercent color={uiColors.primary} style={{ marginLeft: 8, position: 'relative', top: 5 }} />
                                        تخفیف دارها
                                    </Fragment>
                                }
                                defaultValue={searchParams[`has_discount`]}
                                searchParamsKey={'has_discount'}
                                ref={el => { filtersRef.current[`has_discount`] = el }} />

                            <BooleanFilter
                                label={
                                    <Fragment>
                                        <Warehouse color={uiColors.primary} style={{ marginLeft: 8, position: 'relative', top: 5 }} />
                                        فقط کالاهای موجود
                                    </Fragment>
                                }
                                defaultValue={searchParams[`has_quantity`]}
                                searchParamsKey={'has_quantity'}
                                ref={el => { filtersRef.current[`has_quantity`] = el }} />

                        </Fragment>





                    </Box>
                </Box>


                <Box sx={{
                    // position: 'sticky', bottom: 0, right: 0, left: 0,
                    display: 'flex',
                    alignItems: 'center',
                    // bgcolor: '#fff', 
                    // bgcolor: colors.grey[100],
                    zIndex: 1,
                    height: 64,
                    // boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
                    borderTop: `1px solid ${colors.grey[200]}`,

                    px: 1
                }}>
                    <Button variant="contained" disableElevation fullWidth disableRipple
                        onClick={applyFilterAction} sx={{
                            height: 48,
                            borderRadius: '4px'
                        }} endIcon={<FunnelPlus color="#fff" />}>اعمال فیلتر</Button>
                </Box>

            </Drawer>
        </NoSsr>
    </Fragment>
}


const Dot = () => {
    return <Box component="span" sx={{
        position: 'relative',
        pl: 1,
        height: 1,
        display: 'flex', alignItems: 'center', '&::before': {
            content: '""',
            position: 'absolute',
            height: 8, width: 8,
            // top: 9,
            // left: 8,
            // height: 8, 
            // width: 8, 
            bgcolor: uiColors.primary,
            borderRadius: '50%'
        }
    }} />
}

const FilterTitleWrapper = ({ children, used, sx }: {
    children: ReactNode,
    used?: boolean,
    sx?: SxProps
}) => {
    return <Box sx={{
        display: 'flex',
        alignItems: 'center',
        height: 56,
        ...sx
    }}>
        {children}
        {used && (
            <Dot />
        )}
    </Box>
}

const FilterTitle = ({ children, sx }: {
    children: ReactNode,
    sx?: SxProps
}) => {
    return (
        <Typography component="span" sx={{
            fontSize: { xs: 16, sm: 18 },
            fontWeight: 500,
            color: colors.grey[800],
            ...sx
        }}>
            {children}
        </Typography>
    )
}

const StyledAccordion = ({ children, ...props }: AccordionProps) => {
    return <Accordion sx={{
        borderBottom: `1px solid ${colors.grey[300]}`,
        borderRight: 'unset',
        borderLeft: 'unset',
        '&:first-child': {
            borderTop: 0,
        },
        '&:not(:last-child)': {
            // borderBottom: 0,
        },
        '&::before': {
            display: 'none',
        },
        '& .MuiAccordion-heading > button': {
            height: 56
        },
    }} disableGutters elevation={0} square  {...props} >
        {children}
    </Accordion>
}

const WithScrollAccordionDetails = ({ children, ...props }: AccordionDetailsProps) => {
    return (
        <AccordionDetails {...props} sx={{
            padding: 0,
            ...props.sx
        }}>
            {children}
        </AccordionDetails>
    )
}
